import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Save, Plus, ListChecks, Search } from "lucide-react";
import { opsAPI, PRIORITY_STYLE, timeAgo, cls } from "./opsApi";

const STATUS_STYLE = {
  open:        "bg-slate-100 text-slate-700",
  assigned:    "bg-sky-100 text-sky-700",
  in_progress: "bg-amber-100 text-amber-700",
  completed:   "bg-emerald-100 text-emerald-700",
  cancelled:   "bg-slate-100 text-slate-500",
};

const NEXT_STATES = {
  open:        ["assigned","in_progress","completed","cancelled"],
  assigned:    ["in_progress","completed","cancelled"],
  in_progress: ["completed","cancelled"],
  completed:   [],
  cancelled:   [],
};

export default function OpsTasks() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("");
  const [mine, setMine] = useState(false);
  const [q, setQ] = useState("");
  const [creating, setCreating] = useState(false);

  const load = () => opsAPI.tasks({ status: status || undefined, mine }).then(setRows);
  useEffect(() => { load(); }, [status, mine]);   // eslint-disable-line

  const filtered = q ? rows.filter(r => (r.title||"").toLowerCase().includes(q.toLowerCase())) : rows;

  const advance = async (t, next) => {
    try { await opsAPI.updateTask(t.id, { status: next }); load(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="ops-tasks">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2"/>
          <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Search tasks…" aria-label="Search tasks"
                 data-testid="ops-tasks-search"
                 className="h-10 pl-9 pr-3 rounded-lg border border-slate-200 bg-white text-[13px] w-72 focus:border-hocp-navy outline-none"/>
        </div>
        <div className="flex gap-1" data-testid="ops-tasks-tabs">
          {["","open","assigned","in_progress","completed","cancelled"].map(s => (
            <button key={s} onClick={()=>setStatus(s)} data-testid={`ops-tasks-tab-${s || "all"}`}
                    className={`h-9 px-3 rounded-lg text-[12.5px] font-medium border ${
                      status===s ? "bg-hocp-mint text-hocp-deepTeal border-hocp-mint" : "bg-white text-slate-600 border-slate-200"}`}>
              {s ? s.replace("_"," ") : "All"}
            </button>
          ))}
        </div>
        <label className="inline-flex items-center gap-2 text-[13px]">
          <input type="checkbox" checked={mine} onChange={e=>setMine(e.target.checked)} data-testid="ops-tasks-mine"/> Mine only
        </label>
        <button onClick={()=>setCreating(true)} data-testid="ops-tasks-new"
                className="ml-auto h-10 px-4 rounded-lg bg-hocp-gold text-hocp-eerie text-[13px] font-semibold inline-flex items-center gap-2 hover:brightness-95">
          <Plus className="w-4 h-4"/> New Task
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        {filtered.length === 0
          ? <div className="p-12 text-center">
              <ListChecks className="w-12 h-12 mx-auto text-slate-300 mb-2"/>
              <div className="text-[13px] text-slate-500">No tasks in this bucket.</div>
            </div>
          : (
            <table className="w-full text-[13px]">
              <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3">Task</th>
                  <th className="text-center px-4 py-3">Priority</th>
                  <th className="text-left px-4 py-3">Module / Dept</th>
                  <th className="text-left px-4 py-3">Assignee</th>
                  <th className="text-center px-4 py-3">Status</th>
                  <th className="text-left px-4 py-3">Created</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map(t => (
                  <tr key={t.id} data-testid={`ops-tasks-row-${t.id}`} className="hover:bg-slate-50/50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-hocp-navy">{t.title}</div>
                      {t.description && <div className="text-[11.5px] text-slate-500 truncate max-w-md">{t.description}</div>}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-0.5 rounded-md text-[11px] font-medium capitalize ${PRIORITY_STYLE[t.priority]}`}>{t.priority}</span>
                    </td>
                    <td className="px-4 py-3 text-[11.5px] text-slate-500">
                      {t.module && <span className="mono">{t.module}</span>}
                      {t.department && <span> · {t.department}</span>}
                    </td>
                    <td className="px-4 py-3 text-[12px] text-slate-500 truncate max-w-[160px]">{t.assignee_id || "—"}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-0.5 rounded-md text-[11px] font-medium capitalize ${STATUS_STYLE[t.status]}`}>{(t.status||"").replace("_"," ")}</span>
                    </td>
                    <td className="px-4 py-3 text-[11.5px] mono text-slate-400">{timeAgo(t.created_at)}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-1">
                        {(NEXT_STATES[t.status] || []).map(ns => (
                          <button key={ns} onClick={()=>advance(t, ns)} data-testid={`ops-task-${t.id}-to-${ns}`}
                                  className="h-7 px-2 rounded-lg bg-slate-50 text-slate-600 hover:bg-slate-100 text-[11px] font-medium border border-slate-200 capitalize">
                            {ns.replace("_"," ")}
                          </button>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </div>

      {creating && <CreateTask onClose={()=>setCreating(false)} onDone={()=>{ setCreating(false); load(); }}/>}
    </div>
  );
}

function CreateTask({ onClose, onDone }) {
  const [f, setF] = useState({ title:"", description:"", priority:"normal", department:"", module:"" });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!f.title.trim()) { toast.error("Title required"); return; }
    setSaving(true);
    try {
      await opsAPI.createTask({ ...f, department: f.department || null, module: f.module || null });
      toast.success("Task created"); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg" onClick={e=>e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-display text-[16px] font-semibold text-hocp-navy">New Task</h3>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100"><X className="w-4 h-4 text-slate-500"/></button>
        </div>
        <div className="p-5 space-y-3">
          <label className="block"><span className="block text-[11.5px] font-medium text-slate-600 mb-1">Task title</span>
            <input value={f.title} onChange={e=>setF({...f, title: e.target.value})} placeholder="e.g. Restock minibar on floor 3"
                   data-testid="ops-task-title" className={cls + " w-full"}/></label>
          <label className="block"><span className="block text-[11.5px] font-medium text-slate-600 mb-1">Details <span className="text-slate-400 font-normal">(optional)</span></span>
            <textarea value={f.description} onChange={e=>setF({...f, description: e.target.value})} rows={3}
                       placeholder="Add any extra context" data-testid="ops-task-desc"
                       className={cls + " w-full h-auto py-2 resize-y"}/></label>
          <div className="grid grid-cols-3 gap-2">
            <label className="block"><span className="block text-[11.5px] font-medium text-slate-600 mb-1">Priority</span>
              <select value={f.priority} onChange={e=>setF({...f, priority: e.target.value})}
                      data-testid="ops-task-priority" className={cls + " w-full"}>
                {["low","normal","high","critical"].map(p => <option key={p} value={p}>{p}</option>)}
              </select></label>
            <label className="block"><span className="block text-[11.5px] font-medium text-slate-600 mb-1">Department <span className="text-slate-400 font-normal">(opt)</span></span>
              <input value={f.department} onChange={e=>setF({...f, department: e.target.value})}
                     placeholder="e.g. Housekeeping" data-testid="ops-task-dept" className={cls + " w-full"}/></label>
            <label className="block"><span className="block text-[11.5px] font-medium text-slate-600 mb-1">Module <span className="text-slate-400 font-normal">(opt)</span></span>
              <input value={f.module} onChange={e=>setF({...f, module: e.target.value})}
                     placeholder="e.g. Front Office" data-testid="ops-task-module" className={cls + " w-full"}/></label>
          </div>
        </div>
        <div className="px-5 py-3 bg-slate-50 flex justify-end gap-2 border-t border-slate-100">
          <button onClick={onClose} className="h-9 px-3.5 rounded-lg text-slate-600 text-[13px] hover:bg-slate-100">Cancel</button>
          <button onClick={submit} disabled={saving} data-testid="ops-task-submit"
                  className="h-9 px-4 rounded-lg bg-hocp-gold text-hocp-eerie text-[13px] font-medium inline-flex items-center gap-2 disabled:opacity-60">
            <Save className="w-4 h-4"/> {saving ? "Saving…" : "Create Task"}
          </button>
        </div>
      </div>
    </div>
  );
}
